// Budget and cost-to-go by cost code x cost class, off the same forecast data
// the Overview's budget grid reads, so a report outside the Overview (the
// Billing tab's cost report) can carry Budget and EAC columns that match it.
//
//   budget    — Day Zero Budget + approved Change Orders (cost types 5xxxx)
//   remaining — open commitments + ETC: the Materials / Rentals /
//               Subcontractors / Other tabs, the cost-type 'etc' rows the
//               Overview picks up (51xxx beyond 51100, 52300, 52400), the
//               Burn Tool labor and equipment weeks inside the forecast
//               window, and the Contingency tab's current amounts on 101101
//
// EAC is then cost to date + remaining, which is the Overview's actual +
// open commitments + ETC. Cost to date is left to the caller: the cost
// report states it off its own lines, and EAC has to foot to those.
//
// Classes are billingPricing's bucketOf() buckets (Labor / Equipment /
// Materials / Subcontractors / Other), so they line up with the report's
// class columns. The revenue budget (4xxxx Day Zero rows + approved CO
// revenue) comes back on its own for a caller that prices on the bill basis;
// a version with a Revenue Budget tab (helpers/revenueBudget) states it by
// cost code instead, in `revenueByCode`, and its total replaces the 4xxxx
// lines. Each code's approved CO cost comes back as `co` so such a caller can
// price the COs apart from the Day Zero lines the revenue budget covers.
//
// Each code's cost-to-go also comes back by cost type in `types`, with the
// open commitments (`committed`) apart from the forecast's ETC (`etc`) — the
// split the Cost Plus Forecast shows — for a report that grids cost types. A
// forecast row with no cost type (the Contingency tab, a Rentals row left
// blank) keys by what it is instead, e.g. '~Contingency'. `desc` on a code
// and on a type is the open commitment's description, for a code no cost has
// posted to yet.

import moment from 'moment';
import { pNum } from '../db/company';
import { bucketOf } from '../components/billingPricing';
import { fieldBonusRemoved } from './fieldBonus';
import { revenueBudgetByCode } from './revenueBudget';

export const PLAN_CLASSES = ['Labor', 'Equipment', 'Materials', 'Subcontractors', 'Other'];

// The Overview's cost-code key: six digits, anything else rolls to the
// project team code.
export const normCC = (cc) => {
    const s = String(cc == null ? '' : cc).trim();
    return /^\d{6}$/.test(s) ? s : '100101';
};
const skipCode = (cc) => cc === '1000' || cc === '990000';
const isRev = (ct) => String(ct || '').startsWith('4');
const isCost = (ct) => String(ct || '').startsWith('5');

const ETC_TABS = ['Materials', 'Rentals', 'Subcontractors', 'Other', 'Materials-phased', 'Rentals-phased', 'Subcontractors-phased', 'Other-phased'];
const TAB_CLASS = { Materials: 'Materials', Rentals: 'Equipment', Subcontractors: 'Subcontractors', Other: 'Other' };
const BURN_TABS = ['Burn Tool Labor', 'Burn Tool Equipment', 'Burn Tool Labor Phased', 'Burn Tool Equipment Phased'];
const CONTINGENCY_FIELDS = { CurrentLabor: 'Labor', CurrentEquipment: 'Equipment', CurrentMaterials: 'Materials', CurrentSubs: 'Subcontractors', CurrentOther: 'Other' };

// Latest save wins per (version, job, tab, field, row) — the Overview's dedupe.
const dedupe = (rows) => {
    const latest = {};
    rows.forEach((d) => {
        const key = `${d.Version_GUID}_${d.BU_ID}_${d.Forecast_Tab}_${d.Forecast_Field_Name}_${d.Forecast_Row_ID}`;
        const at = Math.max(new Date(d.DSS_Created_Date || d.DSS_Created_Datetime || 0).getTime() || 0, new Date(d.DSS_Updated_Datetime || 0).getTime() || 0);
        if (!latest[key] || at > latest[key].at) latest[key] = { at, d };
    });
    return Object.values(latest).map((x) => x.d);
};

export const jobPlanByCode = ({ userData, version, openCommitments, versionStart, projectComplete } = {}) => {
    const codes = {};
    const node = (cc) => codes[cc] || (codes[cc] = {
        budget: PLAN_CLASSES.reduce((a, k) => { a[k] = 0; return a; }, {}),
        remaining: PLAN_CLASSES.reduce((a, k) => { a[k] = 0; return a; }, {}),
        co: 0,
        types: {},
    });
    // `by` says where cost-to-go sits by cost type: { ct, label, committed }.
    // Burn tool labor also brings its hours (`hrs`) and the ones past 40 a
    // week per person (`otHrs`) — rows price every hour at straight wage, so
    // the overtime only shows in the hours — kept with the cost they came
    // with (`hrsCost`).
    const add = (cc, side, cls, amt, by) => {
        const n = pNum(amt);
        if (!n) return;
        const k = PLAN_CLASSES.includes(cls) ? cls : 'Other';
        node(cc)[side][k] += n;
        if (side !== 'remaining') return;
        const ct = by && isCost(by.ct) ? String(by.ct) : '';
        const key = ct || `~${(by && by.label) || k}`;
        const types = node(cc).types;
        const t = types[key] || (types[key] = { ct, cls: k, label: ct ? '' : ((by && by.label) || k), desc: '', committed: 0, etc: 0, hrs: 0, otHrs: 0, hrsCost: 0 });
        t[by && by.committed ? 'committed' : 'etc'] += n;
        if (by && by.hrs) { t.hrs += by.hrs; t.otHrs += by.otHrs || 0; t.hrsCost += n; }
    };
    let revenueBudget = 0;
    let coRevenue = 0;

    const rows = dedupe((Array.isArray(userData) ? userData : []).filter((d) => d && String(d.Version_GUID) === String(version)));
    const hideFieldBonus = fieldBonusRemoved(versionStart);
    let completion = null;
    rows.forEach((d) => {
        if ((d.Forecast_Tab === 'All' || d.Forecast_Tab === 'Forecast') && d.Forecast_Field_Name === 'completionDateForecast' && d.Forecast_Value) {
            completion = d.Forecast_Value;
        }
    });
    // The forecast window: weeks after the version's start week, up to the
    // job's forecast completion (top-bar date first, as the Overview does).
    const start = moment(versionStart).startOf('week');
    const end = moment(projectComplete || completion).endOf('week');
    const inWindow = (week, { inclusiveEnd = false } = {}) => {
        const w = moment(week);
        if (!w.isValid() || !w.isAfter(start)) return false;
        return inclusiveEnd ? end.isAfter(w) : w.isBefore(end);
    };
    const rateCost = (r) => pNum(r.Rate) * pNum(r.Number_Units) * pNum(r.Number_Hours);
    // A labor row's hours: Number_Hours a person a week × Number_Units people;
    // past 40 a week is overtime (JDE payroll's weekly rule).
    const laborHrs = (r) => ({ hrs: pNum(r.Number_Hours) * pNum(r.Number_Units), otHrs: Math.max(0, pNum(r.Number_Hours) - 40) * pNum(r.Number_Units) });

    rows.forEach((d) => {
        const tab = d.Forecast_Tab;
        const ct = String(d.Cost_Type || '');
        const rawCC = d.Cost_Code == null ? '' : String(d.Cost_Code).trim();

        if (tab === 'Day Zero Budget') {
            if (isRev(ct)) { revenueBudget += pNum(d.Value_Amount); return; }
            if (!rawCC || skipCode(rawCC) || !isCost(ct)) return;
            add(normCC(rawCC), 'budget', bucketOf(ct), d.Value_Amount);
            return;
        }
        if (tab === 'Change Order') {
            if (String(d.Status || d.status || '').toLowerCase() !== 'approved') return;
            if (isRev(ct)) { coRevenue += pNum(d.Value_Amount); return; }
            if (!isCost(ct)) return;
            // No code / 1000 / 990000 on a cost CO routes to the project team.
            const coCC = rawCC && !skipCode(rawCC) ? normCC(rawCC) : '100101';
            add(coCC, 'budget', bucketOf(ct), d.Value_Amount);
            node(coCC).co += pNum(d.Value_Amount);
            return;
        }
        if (tab === 'Contingency') {
            const cls = CONTINGENCY_FIELDS[d.Forecast_Field_Name];
            if (cls) add('101101', 'remaining', cls, d.Forecast_Value, { label: 'Contingency' });
            return;
        }
        if (ETC_TABS.includes(tab)) {
            if (rawCC === '101101' || isRev(ct) || (hideFieldBonus && ct === '51800')) return;
            const cc = rawCC && !skipCode(rawCC) ? normCC(rawCC) : '100101';
            add(cc, 'remaining', ct ? bucketOf(ct) : TAB_CLASS[tab.replace('-phased', '')], d.Value_Amount, { ct, label: tab.replace('-phased', '') });
            return;
        }
        if (tab === 'Burn Tool Equipment') {
            const key = String(d.Forecast_Row_ID || '');
            if (key.length !== 10 || !inWindow(key, { inclusiveEnd: true }) || !rawCC || skipCode(rawCC) || isRev(ct)) return;
            add(normCC(rawCC), 'remaining', 'Equipment', rateCost(d), { ct, label: 'Burn Tool Equipment' });
            return;
        }
        if (tab === 'Burn Tool Labor') {
            const week = String(d.Forecast_Row_ID || '').split('*')[0];
            if (!week || !inWindow(week, { inclusiveEnd: true }) || !rawCC || skipCode(rawCC) || isRev(ct)) return;
            add(normCC(rawCC), 'remaining', 'Labor', rateCost(d), { ct, label: 'Burn Tool Labor', ...laborHrs(d) });
            return;
        }
        if (tab === 'Burn Tool Labor Phased') {
            const key = String(d.Forecast_Row_ID || '');
            if (!key.includes('=')) return;
            const [keyCC, week] = key.split('=');
            if (!inWindow(week)) return;
            const cc = rawCC && rawCC !== 'undefined' ? rawCC : keyCC;
            if (!cc || skipCode(cc) || isRev(ct)) return;
            add(normCC(cc), 'remaining', 'Labor', rateCost(d), { ct, label: 'Burn Tool Labor', ...laborHrs(d) });
            return;
        }
        if (tab === 'Burn Tool Equipment Phased') {
            const key = String(d.Forecast_Row_ID || '');
            const sep = key.includes('=') ? '=' : key.includes('*') ? '*' : null;
            if (!sep) return;
            const at = key.lastIndexOf(sep);
            const phase = key.substring(0, at);
            if (!inWindow(key.substring(at + 1))) return;
            add(phase !== 'undefined' ? normCC(phase) : '100101', 'remaining', 'Equipment', rateCost(d), { ct, label: 'Burn Tool Equipment' });
            return;
        }
        // The cost-type 'etc' rows the tab paths above don't cover: labor
        // sub-types beyond straight time, and fuel / repairs.
        if (d.Forecast_Field_Name === 'etc' && ct && rawCC !== '101101' && !BURN_TABS.includes(tab)) {
            const missingLabor = ct.startsWith('51') && ct !== '51100' && !(hideFieldBonus && ct === '51800');
            const missingEquip = ct === '52300' || ct === '52400';
            if (!missingLabor && !missingEquip) return;
            add(rawCC && !skipCode(rawCC) ? normCC(rawCC) : '100101', 'remaining', bucketOf(ct), d.Value_Amount, { ct });
        }
    });

    (Array.isArray(openCommitments) ? openCommitments : []).forEach((c) => {
        const cc = c.Phase_Code == null ? '' : String(c.Phase_Code).trim();
        if (!cc || skipCode(cc) || !isCost(c.Cost_Type_Code)) return;
        const code = normCC(cc);
        const ct = String(c.Cost_Type_Code).trim();
        add(code, 'remaining', bucketOf(ct), c.PO_True_Open_Amount, { ct, committed: true });
        const n = codes[code];
        if (!n) return;
        if (!n.desc && c.Phase_Code_Description) n.desc = String(c.Phase_Code_Description).trim();
        const t = n.types[ct];
        if (t && !t.desc && c.Cost_Type_Description) t.desc = String(c.Cost_Type_Description).trim();
    });

    const costBudget = Object.values(codes).reduce((a, n) => a + PLAN_CLASSES.reduce((s, k) => s + n.budget[k], 0), 0);
    // The Revenue Budget tab, when the version has one, is the bid revenue by
    // cost code; its total stands in for the 4xxxx lump.
    const rev = revenueBudgetByCode(userData, version);
    const revenueByCode = {};
    Object.entries(rev.byCode).forEach(([cc, amt]) => { const k = normCC(cc); revenueByCode[k] = (revenueByCode[k] || 0) + amt; });
    return {
        codes,
        revenueBudget: (rev.has ? rev.total : revenueBudget) + coRevenue,
        revenueByCode: rev.has ? revenueByCode : null,
        costBudget,
    };
};
